import { Link } from "react-router-dom";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  Compass,
  DraftingCompass,
  Map,
  Plane,
  ScanLine,
  FileText,
  Leaf,
  Briefcase,
  Check,
  ArrowUpRight,
} from "lucide-react";
import { Reveal, SectionLabel, SectionTitle } from "@/components/Reveal";
import { services } from "@/data/site";

const ICONS = {
  Compass,
  DraftingCompass,
  Map,
  Plane,
  ScanLine,
  FileText,
  Leaf,
  Briefcase,
};

export default function Servizi() {
  const { hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 200);
      }
    }
  }, [hash]);

  return (
    <main data-testid="servizi-page" className="bg-slate-50">
      {/* HEADER */}
      <section className="pt-36 pb-16 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <SectionLabel>Servizi</SectionLabel>
            <h1 className="font-serif font-medium mt-5 text-5xl md:text-7xl tracking-tight leading-[1.02] text-slate-900">
              Otto aree.<br />
              <em className="not-italic text-amber-800">Un metodo.</em>
            </h1>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-8 max-w-3xl text-lg text-slate-600 leading-relaxed">
              I prevalenti settori di attività dello studio, garantiti
              dall'esperienza maturata e dal continuo aggiornamento
              professionale.
            </p>
          </Reveal>

          {/* Anchor index */}
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-3">
            {services.map((s) => {
              const Icon = ICONS[s.icon] || Compass;
              return (
                <a
                  key={s.slug}
                  href={`#${s.slug}`}
                  data-testid={`servizi-anchor-${s.slug}`}
                  className="group flex items-center gap-3 px-4 py-3 border border-slate-200 hover:border-slate-900 bg-white transition-colors"
                >
                  <Icon size={18} className="text-amber-800 shrink-0" />
                  <span className="text-sm font-medium text-slate-700 group-hover:text-slate-900">
                    {s.title}
                  </span>
                </a>
              );
            })}
          </div>
        </div>
      </section>

      {/* SECTIONS */}
      {services.map((s, idx) => {
        const Icon = ICONS[s.icon] || Compass;
        const isAlt = idx % 2 === 1;
        return (
          <section
            key={s.slug}
            id={s.slug}
            data-testid={`servizio-section-${s.slug}`}
            className={`py-24 lg:py-32 scroll-mt-24 ${isAlt ? "bg-white" : "bg-slate-50"}`}
          >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <Reveal>
                <div className="flex items-center gap-4 text-amber-800">
                  <Icon size={28} />
                  <span className="overline">Servizio {String(idx + 1).padStart(2, "0")}</span>
                </div>
                <SectionTitle className="mt-4 max-w-4xl">{s.title}</SectionTitle>
              </Reveal>

              <div className="mt-12 grid lg:grid-cols-12 gap-10">
                <div className="lg:col-span-7 space-y-8">
                  {s.body.map((b, i) => (
                    <Reveal key={i} delay={i * 0.05}>
                      <div>
                        <h3 className="font-serif text-2xl text-slate-900">{b.h}</h3>
                        <p className="mt-4 text-base text-slate-700 leading-relaxed whitespace-pre-line">
                          {b.p}
                        </p>
                      </div>
                    </Reveal>
                  ))}
                </div>
                <div className="lg:col-span-5 lg:pl-10 lg:border-l lg:border-slate-200">
                  <Reveal delay={0.1}>
                    <div className="overline mb-5">Servizi offerti</div>
                    <ul className="space-y-3">
                      {s.bullets.map((bl, i) => (
                        <li key={i} className="flex gap-3 text-slate-700">
                          <Check size={18} className="text-amber-800 mt-0.5 shrink-0" />
                          <span className="text-sm leading-relaxed">{bl}</span>
                        </li>
                      ))}
                    </ul>

                    {s.slug === "topografia-geomatica" && (
                      <Link
                        to="/strumentazione"
                        data-testid="servizi-strumentazione-link"
                        className="mt-8 inline-flex items-center gap-2 text-sm font-medium text-slate-900 border-b border-slate-900 pb-1 hover:gap-3 transition-all"
                      >
                        Vedi la strumentazione <ArrowUpRight size={14} />
                      </Link>
                    )}
                  </Reveal>
                </div>
              </div>
            </div>
          </section>
        );
      })}

      {/* CTA */}
      <section className="py-20 bg-slate-900 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <h2 className="font-serif text-4xl md:text-5xl leading-tight">
              Hai bisogno di un servizio specifico?
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <Link
              to="/contatti"
              data-testid="servizi-cta-contact"
              className="mt-8 inline-flex items-center gap-2 bg-amber-300 text-slate-900 px-8 py-4 rounded-full font-medium hover:bg-amber-200 transition-colors"
            >
              Richiedi un preventivo <ArrowUpRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
